/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React from 'react';

// Uses @compiled/react because descendant/child selectors and !important are not supported by the strict @atlaskit/css API.
import { cssMap as compiledCssMap } from '@compiled/react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	dropdownIndicatorWrapper: {
		marginRight: token('space.025'),
		textAlign: 'center',
		width: '32px',
	},
	text: {
		font: token('font.heading.xsmall'),
		marginTop: token('space.100'),
	},
	control: {
		boxShadow: token('elevation.shadow.overlay'),
		display: 'flex',
		paddingTop: token('space.050'),
		paddingRight: token('space.050'),
		paddingBottom: token('space.050'),
		paddingLeft: token('space.050'),
	},
	closeIconWrapper: {
		cursor: 'pointer',
		color: token('color.text.subtlest'),
		'&:hover': {
			color: token('color.text.subtle'),
		},
		'&:focus': {
			color: token('color.text.subtle'),
		},
	},
});

const displayWrapperStyles = cssMap({
	shown: {
		display: 'block',
	},
	hidden: {
		display: 'none',
	},
});

const styledBoxStyles = compiledCssMap({
	box: {
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column',
		height: 'var(--paginated-picker-box-height)',
		justifyContent: 'center',
	},
});

const styledBoxIconStyles = compiledCssMap({
	boxIcon: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- required to size the SVG rendered by the passed-in @atlaskit/icon child
		'& svg': {
			width: 'var(--paginated-picker-icon-size)',
			height: 'var(--paginated-picker-icon-size)',
		},
	},
});

const styledInvalidCheckboxOptionStyles = compiledCssMap({
	invalidCheckboxOption: {
		backgroundColor: token('color.background.danger'),
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		paddingRight: token('space.100'),
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- required to override the background of the nested react-select option child
		'& > *': {
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-important-styles -- required to override react-select's inline option background
			backgroundColor: 'inherit !important',
		},
	},
});

interface StyledBoxProps extends React.ComponentPropsWithoutRef<'div'> {
	height?: number;
	iconSize?: number;
}

interface DisplayWrapperProps extends React.ComponentPropsWithoutRef<'div'> {
	displayFlag?: boolean;
}

export const DropdownIndicatorWrapper = ({
	children,
	...props
}: React.ComponentPropsWithoutRef<'div'>): React.JSX.Element => (
	<div css={styles.dropdownIndicatorWrapper} {...props}>
		{children}
	</div>
);

export const StyledBox = ({
	height,
	iconSize,
	children,
	style,
	...props
}: StyledBoxProps): React.JSX.Element => (
	<div
		css={[styledBoxStyles.box, iconSize ? styledBoxIconStyles.boxIcon : undefined]}
		style={
			{
				'--paginated-picker-box-height': `${height || 128}px`,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- runtime-only custom property derived from the iconSize prop
				...(iconSize ? { '--paginated-picker-icon-size': `${iconSize}px` } : {}),
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- forwards a caller-provided runtime style object
				...style,
			} as React.CSSProperties
		}
		{...props}
	>
		{children}
	</div>
);

export const StyledText = ({
	children,
	...props
}: React.ComponentPropsWithoutRef<'div'>): React.JSX.Element => (
	<div css={styles.text} {...props}>
		{children}
	</div>
);

export const StyledControl: React.ForwardRefExoticComponent<
	React.ComponentPropsWithoutRef<'div'> & React.RefAttributes<HTMLDivElement>
> = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<'div'>>(
	({ children, ...props }, ref) => (
		<div ref={ref} css={styles.control} {...props}>
			{children}
		</div>
	),
);

export const CloseIconWrapper = ({
	children,
	...props
}: React.ComponentPropsWithoutRef<'div'>): React.JSX.Element => (
	<div css={styles.closeIconWrapper} {...props}>
		{children}
	</div>
);

export const DisplayWrapper = ({
	displayFlag,
	children,
	...props
}: DisplayWrapperProps): React.JSX.Element => (
	<div css={displayWrapperStyles[displayFlag ? 'shown' : 'hidden']} {...props}>
		{children}
	</div>
);

export const StyledInvalidCheckboxOption = ({
	children,
	...props
}: React.ComponentPropsWithoutRef<'div'>): React.JSX.Element => (
	<div css={styledInvalidCheckboxOptionStyles.invalidCheckboxOption} {...props}>
		{children}
	</div>
);
